import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  GetAutomationInboundSourcesDocument,
  type AutomationInboundSourceRow,
} from "#app/lib/data";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";

// One inbound-capable connection selectable as a trigger Source, with its
// provider's event topics. The row type comes from the colocated fragment
// through `lib/data`, so the shape follows the schema and cannot drift.
export type InboundSource = AutomationInboundSourceRow;

// The workspace's active inbound-capable connections, for the trigger builder's
// Source dropdown. `byId` round-trips a saved connection-sourced trigger —
// resolving its stored `config.connectionId` back to a name + the provider's
// topics for the Event dropdown.
//
// cache-and-network (not cache-first): a connection can be added, renamed, or
// disabled on the integrations surface while the builder is open in another
// tab, and a stale list would offer a source that no longer exists.
//
// Skipped without `automations.manage` — the server gates the query on it, and
// everyone can VIEW an automation, so the label surfaces (the workflow popover,
// the read-only canvas captions) would otherwise fire a request that can only
// come back FORBIDDEN and toast at a viewer who asked for nothing. They already
// degrade to the generic catalog label when the list is unknown.
export function useAutomationInboundSources() {
  const { canEdit } = useAutomationPermissions();
  const { data, loading, error } = useQuery(GetAutomationInboundSourcesDocument, {
    fetchPolicy: "cache-and-network",
    skip: !canEdit,
  });

  const sources = useMemo<InboundSource[]>(
    () => data?.automationInboundSources ?? [],
    [data],
  );
  const byId = useMemo(
    () => new Map(sources.map((s) => [s.connectionId, s])),
    [sources],
  );

  // A viewer who may not read the list is reported as still LOADING rather than
  // as holding an empty one: every consumer's "is this connection gone?" branch
  // keys off this, and "not permitted to know" must not resolve to "gone".
  return { sources, byId, isLoading: loading || !canEdit, error };
}

// The display name for a connection in prose: PROVIDER + connection name
// ("AccuLynx SW Contracts"). The connection name alone is often a bare
// abbreviation the admin chose ("WF", "RL", "SW Contracts") that says nothing
// about which system it talks to, so every sentence naming a connection leads
// with the provider.
//
// When the connection name ALREADY starts with the provider the name is used
// as-is — admins commonly name connections "AccuLynx", "AccuLynx East" or
// "AccuLynx-East", and prefixing would stutter ("AccuLynx AccuLynx East").
// Matched case- and padding-insensitively, on a word boundary so a provider that
// merely shares a prefix with the name ("RoofLink" vs "RoofLinkPro") still gets
// qualified.
export function connectionDisplayName(source: {
  providerName: string;
  connectionName: string;
}): string {
  const provider = source.providerName.trim();
  const connection = source.connectionName.trim();
  const lowerConnection = connection.toLowerCase();
  const lowerProvider = provider.toLowerCase();
  // Named exactly after the provider → the provider's own casing wins, so a
  // connection typed "acculynx" still reads "AccuLynx".
  if (lowerConnection === lowerProvider) return provider;
  // Already leads with the provider → the admin's full name, as typed. The
  // boundary is any character that is neither a letter nor a digit, not a
  // literal space: admins write "RoofLink-East" and "acculynx_sw" as readily as
  // "RoofLink East", and a space-only test prefixed those again into "RoofLink
  // RoofLink-East". Spelled as "not alphanumeric" rather than `\W`, because `_`
  // is a word character and is one of the separators people actually use. A
  // letter still qualifies the name, so "RoofLinkPro" — a different system that
  // merely shares a prefix — keeps its provider.
  if (
    lowerConnection.startsWith(lowerProvider) &&
    /^[^\p{L}\p{N}]/u.test(lowerConnection.slice(lowerProvider.length))
  ) {
    return connection;
  }
  return `${provider} ${connection}`;
}

// The trigger shape these helpers classify. Kept structural so a caller can
// pass a saved automation's triggers, a graph model's, or a single draft.
type TriggerLike = { sourceKey?: string | null; config?: unknown } | null;

// The connection id a connection-sourced trigger points at, or null for any
// other trigger. The catalog key is a literal here rather than an import from
// trigger-sentence.tsx — that module imports THIS one, and a cycle would leave
// the constant undefined at module-init time.
export function inboundConnectionIdOf(trigger: TriggerLike): string | null {
  if (trigger?.sourceKey !== "integration.inbound") return null;
  const cfg = trigger.config as { connectionId?: unknown } | null | undefined;
  return cfg && typeof cfg === "object" && typeof cfg.connectionId === "string"
    ? cfg.connectionId
    : null;
}

// Whether an automation's SOURCE connection is disabled — for any
// `integration.inbound` trigger, resolve `config.connectionId` and check
// `active === false`.
//
// Such an automation cannot run whatever its own `enabled` flag says: the
// inbound webhook bails on an inactive connection before it ever dispatches. So
// surfaces treat it as disabled — status chip, blocked toggle, tooltip — WITHOUT
// touching the stored `enabled` flag, so re-enabling the connection restores it
// exactly as the author left it.
//
// True only when the source is found AND inactive. A missing source (deleted, or
// a provider since feature-gated) is NOT reported as disabled — we can't
// distinguish "gone" from "still loading", and flashing a false "Connection
// disabled" on every mount would be worse than saying nothing.
export function useAutomationConnectionDisabled(): (
  triggers?: TriggerLike[] | null,
) => boolean {
  const { byId } = useAutomationInboundSources();
  return useMemo(
    () =>
      (triggers?: TriggerLike[] | null): boolean => {
        for (const t of triggers ?? []) {
          const id = inboundConnectionIdOf(t ?? null);
          if (!id) continue;
          const source = byId.get(id);
          if (source && source.active === false) return true;
        }
        return false;
      },
    [byId],
  );
}

// A `resolve(entityType, id)` compatible with `summarizeTrigger` that names a
// connection-sourced trigger's two parts: `connection` (connectionId → its
// provider-qualified display name) and `inboundTopic` (topic key → its friendly
// event label, deduped across every connection's provider events — topic keys
// are global to a provider, and the summary has no connection context to narrow
// by).
//
// Returns null for anything else, so a surface chains it after its own entity
// cases rather than replacing them. Shared by the canvas node caption, the
// read-only text summary, and the builder's own draft name, so all three word a
// connection trigger identically.
export function useInboundSourceResolver(): (entityType: string, id: string) => string | null {
  const { byId } = useAutomationInboundSources();
  return useMemo(() => {
    const topicLabels = new Map<string, string>();
    for (const s of byId.values()) {
      for (const e of s.events) if (!topicLabels.has(e.key)) topicLabels.set(e.key, e.label);
    }
    return (entityType: string, id: string): string | null => {
      if (entityType === "connection") {
        const source = byId.get(id);
        return source ? connectionDisplayName(source) : null;
      }
      if (entityType === "inboundTopic") return topicLabels.get(id) ?? null;
      return null;
    };
  }, [byId]);
}
